// src/services/featureFlagService.js
import flagsmith from 'flagsmith';

// Feature flag constants
export const FEATURES = {
  PROFILE_V2: 'profile_v2',
  STRIPE_PAYMENTS: 'stripe_payments',
  INTERACTIVE_DEMO: 'interactive_demo',
  // Keep Discover on the swipe deck until the map/list experience is ready.
  PROVIDER_EXPLORE_ENABLED: 'provider_explore_enabled',
  // Opt in to the provider account/services landing page. OFF preserves the
  // existing landing page; service creation continues to use its current flow.
  // Read through useProviderServiceLandingFlag so remote rollback is reactive.
  PROVIDER_SERVICE_LANDING: 'provider_service_landing',
  // Optional 50/50 account-first/service-first test inside the flagged entry.
  // OFF shows account-first without counting rollout traffic as an experiment.
  PROVIDER_SERVICE_LANDING_SPLIT_TEST: 'provider_service_landing_split_test',
  // When ON, provider right-swipes and provider listing-detail "Express
  // interest" open the InterviewRequestForm modal instead of immediately
  // posting to /matching/swipe. Backend mirrors this with the same flag name.
  // Default OFF in prod until launch day.
  INTERVIEW_REQUEST_FORM_ENABLED: 'interview_request_form_enabled',
  // When ON, /company/listings renders the legacy editor (ListingDetailsStep
  // plus the portaled preview). OFF, the default, renders the listing card
  // surface. Default OFF in prod, so the new surface is what ships.
  //
  // THE NAME IS NEGATIVE ON PURPOSE. Do not "improve" it into
  // `new_listing_editor`. Resolution below is localStorage, then
  // REACT_APP_FLAG_*, then Flagsmith, then defaultFlags, and the last two are
  // the same call: hasFeature() returns FALSE when Flagsmith is unreachable.
  // A positively-named flag defaulting to on would therefore drop every
  // recruiter back onto the legacy editor the moment Flagsmith was down. With
  // this polarity an unreachable Flagsmith yields the NEW path, and returning
  // to legacy is always a deliberate act:
  //   localStorage.setItem('flag:legacy_listing_editor', 'true')
  LEGACY_LISTING_EDITOR: 'legacy_listing_editor',
  // When ON, the anonymous /providers card builder's credential chip picker
  // (CardFactSheets.jsx) offers CAA alongside CRNA / Anesthesiologist / SRNA.
  // OFF, the default, hides the CAA chip so no visitor can self-select it as
  // a new identity. This is a frontend-only display gate: the backend keeps
  // full CAA support (ingestion, matching, legacy providers, RegisterDirectDto)
  // regardless of this flag, and an existing CAA provider's data is untouched.
  // Flip on when CAA self-registration is ready to ship; no code change needed.
  CAA_PROVIDER_TYPE_ENABLED: 'caa_provider_type_enabled',
  // Provider registration offers the private medical-student path for people
  // committed to anesthesiology. This additive path is default ON; an explicit
  // local, build-time, or remote false remains the rollback control.
  MEDICAL_STUDENT_ANESTHESIOLOGY_ENABLED:
    'medical_student_anesthesiology_enabled',
  // When ON, /company/dashboard renders the activation board as its hero, the
  // nav discloses progressively, the priority-actions list drops to the three
  // urgent rows, and the zero-listing redirect to Settings is gone. OFF, the
  // default, is today's dashboard byte for byte.
  //
  // POSITIVELY NAMED on purpose, which is the opposite call from
  // LEGACY_LISTING_EDITOR above. Resolution ends at hasFeature(), which answers
  // false when Flagsmith is unreachable, so an outage here lands on the surface
  // that has been in production for months rather than on the new one.
  //
  // Read it through useCompanyJourneyFlag (src/hooks), never with a bare
  // isFeatureEnabled call in a component: a rollback lever that cannot reach an
  // already-open dashboard is not a lever.
  COMPANY_JOURNEY: 'company_journey',
  // When ON *together with* COMPANY_JOURNEY, /company/dashboard renders the
  // company progress board (four milestones, tiles, focus card, checklist).
  // With only COMPANY_JOURNEY on, NO board renders: the legacy journey kit was
  // deleted in Phase 5 and its route now answers 410, so the two flags are one
  // lever in practice (D13) and rolling this one back alone costs the board
  // rather than resurrecting the old one. With neither, today's dashboard
  // renders byte for byte.
  //
  // TWO FLAGS ON PURPOSE (D1.12.4). COMPANY_JOURNEY stays the master gate for
  // journey-driven disclosure across the nav and the priority list; this one
  // only selects which board draws. That split is what lets a rollback drop
  // back to the old board without also switching the nav back.
  //
  // POSITIVELY NAMED, like COMPANY_JOURNEY and unlike LEGACY_LISTING_EDITOR:
  // resolution ends at hasFeature(), which answers false when Flagsmith is
  // unreachable, so an outage lands on the surface that has been in production
  // for months rather than on the new one.
  //
  // Read it through useCompanyProgressFlag (src/hooks), never with a bare
  // isFeatureEnabled call in a component: a rollback lever that cannot reach an
  // already-open dashboard is not a lever.
  COMPANY_PROGRESS_BOARD: 'company_progress_board',
};

/**
 * Service for managing feature flags
 */
const listeners = [];

const featureFlagService = {
  /**
   * Initialize the feature flag client
   * @param {string} environmentID - Flagsmith environment ID
   * @returns {Promise} - Promise that resolves when initialization is complete
   */
  init: (environmentID) => {
    return flagsmith.init({
      environmentID,
      cacheFlags: true,
      enableLogs: process.env.NODE_ENV === 'development',
      defaultFlags: {
        [FEATURES.STRIPE_PAYMENTS]: true, // Enable for development
        [FEATURES.PROFILE_V2]: false, // Default to disabled if server is unreachable
        [FEATURES.INTERACTIVE_DEMO]: false,
        [FEATURES.PROVIDER_EXPLORE_ENABLED]: false,
        [FEATURES.PROVIDER_SERVICE_LANDING]: false,
        [FEATURES.PROVIDER_SERVICE_LANDING_SPLIT_TEST]: false,
        [FEATURES.INTERVIEW_REQUEST_FORM_ENABLED]: false,
        [FEATURES.LEGACY_LISTING_EDITOR]: false,
        [FEATURES.CAA_PROVIDER_TYPE_ENABLED]: false,
        [FEATURES.MEDICAL_STUDENT_ANESTHESIOLOGY_ENABLED]: {
          enabled: true,
          value: null,
        },
        [FEATURES.COMPANY_JOURNEY]: false,
        [FEATURES.COMPANY_PROGRESS_BOARD]: false,
      },
      // Wire Flagsmith change notifications to our listeners registry
      onChange: () => {
        listeners.forEach((cb) => {
          try { cb(); } catch (e) { /* no-op */ }
        });
      },
    });
  },

  /**
   * Check if a feature is enabled.
   *
   * Resolution order (first wins):
   *   1. `localStorage.flag:<name>` set to "true" or "false" (runtime toggle
   *      for local dev; survives reload, no restart needed). Toggle via:
   *        localStorage.setItem('flag:interview_request_form_enabled', 'true')
   *   2. `REACT_APP_FLAG_<UPPER_NAME>` env var (build-time override; matches
   *      the backend env-var pattern in feature-flag.service.ts).
   *   3. Flagsmith remote / defaultFlags (the normal path). Callers may pass a
   *      fallback for a feature that should be enabled when the remote flag is
   *      unavailable or absent; an explicit remote false still wins.
   *
   * @param {string} featureName - Name of the feature to check
   * @param {{fallback?: boolean}} [options] - Flagsmith lookup options
   * @returns {boolean} - Whether the feature is enabled
   */
  isFeatureEnabled: (featureName, options) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const ls = window.localStorage.getItem(`flag:${featureName}`);
        if (ls === 'true') return true;
        if (ls === 'false') return false;
      } catch (e) { /* private mode / blocked */ }
    }
    const envKey = `REACT_APP_FLAG_${featureName.toUpperCase()}`;
    const envVal = process.env[envKey];
    if (envVal === 'true') return true;
    if (envVal === 'false') return false;
    return flagsmith.hasFeature(featureName, options);
  },

  /**
   * Get the value of a feature flag
   * @param {string} featureName - Name of the feature
   * @returns {any} - Value of the feature flag
   */
  getFeatureValue: (featureName) => {
    return flagsmith.getValue(featureName);
  },

  /**
   * Update feature flags from the server
   * @returns {Promise} - Promise that resolves when flags are updated
   */
  onChange: (cb) => {
    if (typeof cb !== 'function') return () => {};
    listeners.push(cb);
    // Return unsubscribe
    return () => {
      const idx = listeners.indexOf(cb);
      if (idx !== -1) listeners.splice(idx, 1);
    };
  },

  updateFlags: () => {
    const currentIdentity = flagsmith.getState().identity || 'anonymous';
    return flagsmith.identify(currentIdentity);
  },
};

export default featureFlagService;
